import type { TFunction } from 'i18next'
import type { UserRef } from '../shift/types'

export interface ShiftRef {
  id: string
  scheduleDate?: string
  startAt?: string
  endAt?: string
  shiftTypeCode?: string
  status?: string
  schedule_date?: string
  start_at?: string
  end_at?: string
  shift_type_code?: string
}

export interface ShiftRequest {
  id: string
  tenantId?: string
  requesterId?: string
  requester?: UserRef
  scheduleId?: string
  schedule?: ShiftRef
  requestType?: 'leave' | 'change'
  status: 'pending' | 'approved' | 'rejected'
  reason?: string
  payload?: { proposed_start_at: string; proposed_end_at: string } | null
  reviewerId?: string
  reviewer?: UserRef
  reviewedAt?: string
  reviewerNotes?: string
  creator?: UserRef
  updater?: UserRef
  createdAt?: string
  updatedAt?: string
  // snake_case variants from API
  tenant_id?: string
  requester_id?: string
  schedule_id?: string
  request_type?: string
  reviewer_id?: string
  reviewed_at?: string
  reviewer_notes?: string
  created_at?: string
  updated_at?: string
}

export interface ShiftRequestCreateData {
  schedule_id: string
  request_type: 'leave' | 'change'
  reason: string
  payload?: {
    proposed_start_at: string  // ISO 8601 with timezone
    proposed_end_at: string    // ISO 8601 with timezone
  }
}

export interface ShiftRequestReviewData {
  reviewer_notes?: string
}

export const REQUEST_TYPE_VALUES = ['leave', 'change'] as const
export const REQUEST_STATUS_VALUES = ['pending', 'approved', 'rejected'] as const

// code → i18n key（shifts namespace，带 ns 前缀所以任何 t 都能解析）。label 不做模块级快照：
// 筛选项 / 徽标都在 render 时经 requestTypeLabel / requestStatusLabel 求值，切语言后不会过期
// （见 src/config/upsell-request-filters.ts 的注释）。
export const REQUEST_TYPE_I18N_KEYS: Record<(typeof REQUEST_TYPE_VALUES)[number], string> = {
  leave: 'shifts:shiftRequest.type.leave',
  change: 'shifts:shiftRequest.type.change',
}

export const REQUEST_STATUS_I18N_KEYS: Record<(typeof REQUEST_STATUS_VALUES)[number], string> = {
  pending: 'shifts:shiftRequest.status.pending',
  approved: 'shifts:shiftRequest.status.approved',
  rejected: 'shifts:shiftRequest.status.rejected',
}

/** 申请类型显示文案；字典外的 code（历史脏数据）原样回落，空值返回 ''，由调用方决定占位符。 */
export function requestTypeLabel(code: string | null | undefined, t: TFunction): string {
  if (!code) return ''
  const key = (REQUEST_TYPE_I18N_KEYS as Record<string, string>)[code]
  return key ? t(key) : code
}

/** 申请状态显示文案；规则同 requestTypeLabel。 */
export function requestStatusLabel(code: string | null | undefined, t: TFunction): string {
  if (!code) return ''
  const key = (REQUEST_STATUS_I18N_KEYS as Record<string, string>)[code]
  return key ? t(key) : code
}

export const REQUEST_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 dark:bg-yellow-950/50 text-yellow-800',
  approved: 'bg-green-100 dark:bg-green-950/50 text-green-800',
  rejected: 'bg-red-100 dark:bg-red-950/50 text-red-800',
}

export const REQUEST_TYPE_COLORS: Record<string, string> = {
  leave: 'bg-orange-100 dark:bg-orange-950/50 text-orange-800',
  change: 'bg-blue-100 dark:bg-blue-950/50 text-blue-800',
}
